import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui';
import { CourtHireReceiptLink } from '@/components/member/CourtHireReceiptLink';
import { formatCurrency, type Currency } from '@/utils/currency';
import type { ConsumerCalendarExtraEvent } from '@/utils/consumerCalendarEvents';
import { useOrgMemberStore } from '@/store/orgMember';

interface CourtHireBookingDetailPopoverProps {
  event: ConsumerCalendarExtraEvent | null;
  isOpen: boolean;
  onClose: () => void;
  bookingsListPath?: string | null;
}

function formatCents(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

export function CourtHireBookingDetailPopover({
  event,
  isOpen,
  onClose,
  bookingsListPath,
}: CourtHireBookingDetailPopoverProps) {
  const clubSlug = useOrgMemberStore((s) => s.clubSlug);

  if (!event) return null;

  const start = new Date(event.raw.startAt);
  const end = new Date(event.raw.endAt);
  const dateLabel = start.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeLabel = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{event.title}</DialogTitle>
          <DialogDescription>Court hire booking</DialogDescription>
        </DialogHeader>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="font-medium text-muted-foreground">Date</dt>
            <dd>{dateLabel}</dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">Time</dt>
            <dd>{timeLabel}</dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">Status</dt>
            <dd className="capitalize">{event.status}</dd>
          </div>
          {event.priceCents != null && event.currency ? (
            <div>
              <dt className="font-medium text-muted-foreground">Price</dt>
              <dd>{formatCents(event.priceCents, event.currency)}</dd>
            </div>
          ) : null}
          {clubSlug && event.raw.hasReceipt ? (
            <div>
              <dt className="font-medium text-muted-foreground">Payment</dt>
              <dd>
                <CourtHireReceiptLink
                  clubSlug={clubSlug}
                  bookingId={event.id}
                  hasReceipt={event.raw.hasReceipt}
                />
              </dd>
            </div>
          ) : null}
          {event.equipmentHires.length > 0 ? (
            <div>
              <dt className="font-medium text-muted-foreground">Equipment</dt>
              <dd>
                <ul className="mt-1 space-y-1">
                  {event.equipmentHires.map((hire) => (
                    <li key={hire.id}>
                      {hire.itemName}
                      {hire.quantity > 1 ? ` ×${hire.quantity}` : ''}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
        </dl>
        <DialogFooter className="gap-2 sm:gap-0">
          {bookingsListPath ? (
            <Button asChild variant="outline">
              <Link to={bookingsListPath} onClick={onClose}>
                View all bookings
              </Link>
            </Button>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
